import { PauseCover } from "aic";
import { getHierarchyBaseUrl } from "contexts/hierarchy/getHierarchyBaseUrl";
import { HierarchyContext } from "contexts/HierarchyContext";
import { eHierarchyLevel } from "functions/api/enums";
import { useContext, useEffect, useState, useTransition } from "react";
import { NavDropdown } from "react-bootstrap";
import { Link, useLocation } from "react-router-dom";

interface NewItemDropMenuProps {
  fileEntry?: React.ReactElement;
}

/**
 * Dropdown menu for creating new items (table, listing, figure).
 *
 * @param props - Destructured props for the new-item menu.
 * @param props.fileEntry - Optional element to display additional file actions.
 * @returns A JSX element rendering the new-item dropdown.
 */
export const NewItemDropMenu = ({ fileEntry }: NewItemDropMenuProps): React.ReactElement => {
  const [_isPending, startTransition] = useTransition();
  const hierarchyContext = useContext(HierarchyContext);
  const hierarchyBaseUrl =
    hierarchyContext &&
    hierarchyContext.hierarchy &&
    hierarchyContext.hierarchy.level !== eHierarchyLevel.Study
      ? getHierarchyBaseUrl(hierarchyContext.hierarchy)
      : // istanbul ignore next
        "/Corporate";

  const location = useLocation();
  const [isLoading, setIsLoading] = useState(false);

  /**
   * Handle click on navigation links and set loading state unless modifier keys used.
   *
   * @param e - Click event from the nav item.
   */
  const handleNavClick = (e: React.MouseEvent<HTMLElement, MouseEvent>) => {
    /* istanbul ignore else */
    if (!(e.ctrlKey || e.metaKey || e.button === 1 || e.shiftKey)) {
      setIsLoading(true);
    }
  };

  useEffect(() => {
    /* istanbul ignore else */
    if (location.key && location.pathname) {
      startTransition(() => setIsLoading(false));
    }
  }, [location.key, location.pathname]);

  return (
    <NavDropdown
      id="topbar-file"
      title="File"
      style={{ paddingLeft: "12px", position: "relative" }}
    >
      {isLoading && <PauseCover text="Loading New Template..." />}
      <NavDropdown.Item
        id="topbar-new-table-link"
        as={Link}
        to={`${hierarchyBaseUrl}/NewTlf/newTable`}
        onClick={handleNavClick}
      >
        New Table
      </NavDropdown.Item>
      <NavDropdown.Item
        id="topbar-new-listing-link"
        as={Link}
        to="/Corporate/AZLBL01/2"
        onClick={handleNavClick}
      >
        New Listing
      </NavDropdown.Item>
      <NavDropdown.Item
        id="topbar-new-figure-link"
        as={Link}
        to={`${hierarchyBaseUrl}/NewTlf/newFigure`}
        onClick={handleNavClick}
      >
        New Interactive Figure
      </NavDropdown.Item>
      <NavDropdown.Item
        id="topbar-new-v1figure-link"
        as={Link}
        to={`${hierarchyBaseUrl}/NewTlf/newV1Figure`}
        onClick={handleNavClick}
      >
        New Static Figure
      </NavDropdown.Item>
      {fileEntry !== undefined && <>{fileEntry}</>}
    </NavDropdown>
  );
};

NewItemDropMenu.displayName = "NewItemDropMenu";
